
// PocketGitaBook_v5.js
import React, { useEffect, useState } from 'react';
import { View, Text, Pressable, ScrollView } from 'react-native';
import BookFlipView from './BookFlipView_v2';
import { API, getMeta, getPage } from './pocketGitaApi';

function pickFields(v) {
  const translation =
    v.translation_tadatmananda_en != null
      ? v.translation_tadatmananda_en
      : v.english != null
      ? v.english
      : null;

  const translit =
    v.iast != null
      ? v.iast
      : v.transliteration != null
      ? v.transliteration
      : null;

  return {
    sanskrit: v.sanskrit || null,
    transliteration: translit,
    translation,
  };
}

async function riffle(fromPage, dir) {
  const r = await fetch(`${API}/api/riffle?from_page=${fromPage}&dir=${dir}`);
  if (!r.ok) throw new Error('riffle failed');
  return r.json();
}

async function shuffleAny() {
  const r = await fetch(`${API}/api/shuffle?mode=any`);
  if (!r.ok) throw new Error('shuffle failed');
  return r.json();
}

export default function PocketGitaBook_v5() {
  const [meta, setMeta] = useState(null);
  const [open, setOpen] = useState(false);
  const [rightPg, setRightPg] = useState(null);     // current right page
  const [rightData, setRightData] = useState(null);
  const [leftData, setLeftData] = useState(null);
  const [err, setErr] = useState(null);
  const [toggles, setToggles] = useState({ sanskrit: false, transliteration: false, translation: true });

  useEffect(() => { getMeta().then(setMeta).catch(() => {}); }, []);

  async function loadPair(p) {
    const total = meta?.total_pages || 255;
    const right = Math.min(total, Math.max(1, p|0));
    const left = right > 1 ? right - 1 : null;

    setErr(null);
    try {
      const [rData, lData] = await Promise.all([
        getPage(right),
        left ? getPage(left) : Promise.resolve(null),
      ]);
      setRightPg(right);
      setRightData(rData);
      setLeftData(lData);
    } catch (e) {
      setErr(String(e?.message || e));
    }
  }

  async function onOpenRequested() {
    if (open) return;
    setOpen(true);
  }

  async function onOpened() {
    const { page } = await shuffleAny();
    await loadPair(page);
  }

  // Gestures (keep but no verbose labels)
  async function onFlipForwardRandom() {
    const from = rightPg || 1;
    const { page } = await riffle(from, 'forward');
    await loadPair(page);
  }
  async function onFlipBackwardRandom() {
    const from = rightPg || 1;
    const { page } = await riffle(from, 'back');
    await loadPair(page);
  }

  // Buttons
  async function onPrevSpread() {
    const targetRight = Math.max(1, (rightPg || 1) - 2);
    await loadPair(targetRight);
  }
  async function onNextSpread() {
    const total = meta?.total_pages || 255;
    const targetRight = Math.min(total, (rightPg || 1) + 2);
    await loadPair(targetRight);
  }
  async function onShuffle() {
    const { page } = await shuffleAny();
    await loadPair(page);
  }

  const totalPages = meta?.total_pages || 255;
  const leftPg = leftData?.page || (rightPg ? Math.max(1, rightPg - 1) : 1);

  return (
    <View style={{ flex: 1, backgroundColor: '#FAF5E4', alignItems: 'center', justifyContent: 'center' }}>
      {!open && (
        <View style={{ position: 'absolute', top: 18, alignItems: 'center' }}>
          <Text style={{ fontSize: 12, opacity: 0.6 }}>Swipe to open • API: {API}</Text>
        </View>
      )}

      <BookFlipView
        width={Math.min(740, Math.max(360, (typeof window !== 'undefined' ? window.innerWidth : 640) - 60))}
        height={Math.min(560, Math.max(320, (typeof window !== 'undefined' ? window.innerHeight : 520) - 180))}
        open={open}
        onOpenRequested={onOpenRequested}
        onOpened={onOpened}
        onFlipNext={onFlipForwardRandom}   // → forward random
        onFlipPrev={onFlipBackwardRandom}  // ← backward random
      >
        {open && (
          <View style={{ flex: 1, flexDirection: 'row' }}>
            {/* LEFT PAGE (scrollable) */}
            <View style={{ flex: 1 }}>
              <View style={{ position: 'absolute', left: 12, top: 10, backgroundColor: '#EADFC3', paddingHorizontal: 8, paddingVertical: 4, borderRadius: 999, zIndex: 2 }}>
                <Text style={{ fontSize: 12, fontWeight: '700' }}>Pg {leftPg}</Text>
              </View>
              <ScrollView style={{ flex: 1 }} contentContainerStyle={{ paddingTop: 32, paddingHorizontal: 16, paddingBottom: 20 }}>
                {leftData?.verses?.map((v) => {
                  const pf = pickFields(v);
                  const hasAny = (pf.sanskrit || pf.transliteration || pf.translation);
                  return (
                    <View key={'L'+v.id} style={{ marginBottom: 10, paddingBottom: 10, borderBottomWidth: 1, borderColor: '#E9DFC6' }}>
                      <Text style={{ fontSize: 12, opacity: 0.7 }}>Ch {v.chapter} • V {v.verse}</Text>
                      {toggles.sanskrit && pf.sanskrit && (
                        <Text style={{ fontSize: 20, lineHeight: 26, marginTop: 6, fontFamily: 'Noto Serif Devanagari, Noto Sans Devanagari, serif' }}>
                          {pf.sanskrit}
                        </Text>
                      )}
                      {toggles.transliteration && pf.transliteration && (
                        <Text style={{ fontSize: 15, lineHeight: 22, marginTop: 6 }}>{pf.transliteration}</Text>
                      )}
                      {toggles.translation && pf.translation && (
                        <Text style={{ fontSize: 15, lineHeight: 22, marginTop: 6 }}>{pf.translation}</Text>
                      )}
                      {!hasAny && <Text style={{ fontSize: 12, opacity: 0.6, marginTop: 6 }}>(…)</Text>}
                    </View>
                  );
                })}
              </ScrollView>
            </View>

            {/* RIGHT PAGE (scrollable + minimal controls) */}
            <View style={{ flex: 1 }}>
              {/* Page badge */}
              <View style={{ position: 'absolute', right: 12, top: 10, backgroundColor: '#EADFC3', paddingHorizontal: 10, paddingVertical: 4, borderRadius: 999, zIndex: 2 }}>
                <Text style={{ fontSize: 12, fontWeight: '700' }}>Pg {rightPg} / {totalPages}</Text>
              </View>

              {/* Bottom controls (only Prev • Next • Shuffle) */}
              <View style={{ position: 'absolute', right: 10, bottom: 8, left: 10, zIndex: 3, alignItems: 'center' }}>
                <View style={{ backgroundColor: 'rgba(250,245,228,0.9)', borderRadius: 10, paddingHorizontal: 12, paddingVertical: 8, flexDirection: 'row', gap: 14, justifyContent: 'center', alignItems: 'center' }}>
                  <SmallBtn label="Prev" onPress={onPrevSpread} />
                  <SmallBtn label="Next" onPress={onNextSpread} />
                  <SmallBtn label="Shuffle" onPress={onShuffle} />
                </View>
                {/* Tiny hint row (minimal) */}
                <Text style={{ fontSize: 11, opacity: 0.55, marginTop: 4 }}>{'<- Swipe'}   {'Swipe ->'}</Text>
              </View>

              <ScrollView
                style={{ flex: 1 }}
                contentContainerStyle={{ paddingTop: 24, paddingHorizontal: 16, paddingBottom: 56 }}
                stickyHeaderIndices={[0]}
              >
                {/* Sticky toggles bar */}
                <View style={{ backgroundColor: '#FCF7E8', paddingTop: 0, paddingBottom: 8 }}>
                  <View style={{ flexDirection: 'row', gap: 8 }}>
                    <Chip on={toggles.sanskrit} label="Sanskrit" onPress={() => setToggles(t => ({ ...t, sanskrit: !t.sanskrit }))} />
                    <Chip on={toggles.transliteration} label="IAST" onPress={() => setToggles(t => ({ ...t, transliteration: !t.transliteration }))} />
                    <Chip on={toggles.translation} label="Translation" onPress={() => setToggles(t => ({ ...t, translation: !t.translation }))} />
                  </View>
                </View>

                {err && <Text style={{ color: '#862E2E', marginTop: 8 }}>Error: {err}</Text>}
                {rightData?.verses?.map((v) => {
                  const pf = pickFields(v);
                  const hasAny = (pf.sanskrit || pf.transliteration || pf.translation);
                  return (
                    <View key={'R'+v.id} style={{ marginBottom: 14, paddingBottom: 14, borderBottomWidth: 1, borderColor: '#E9DFC6' }}>
                      <Text style={{ fontSize: 13, opacity: 0.7 }}>Chapter {v.chapter} • Verse {v.verse}</Text>
                      {toggles.sanskrit && pf.sanskrit && <Text style={{ fontSize: 20, lineHeight: 26, marginTop: 6 }}>{pf.sanskrit}</Text>}
                      {toggles.transliteration && pf.transliteration && <Text style={{ fontSize: 15, lineHeight: 22, marginTop: 6 }}>{pf.transliteration}</Text>}
                      {toggles.translation && pf.translation && <Text style={{ fontSize: 15, lineHeight: 22, marginTop: 8 }}>{pf.translation}</Text>}
                      {!hasAny && <Text style={{ fontSize: 13, opacity: 0.6, marginTop: 8 }}>(Content placeholder)</Text>}
                    </View>
                  );
                })}
              </ScrollView>
            </View>
          </View>
        )}
      </BookFlipView>

      <View style={{ position: 'absolute', bottom: 12 }}>
        <Text style={{ fontSize: 12 }}>Translation © Arsha Bodha Center — private gift build.</Text>
      </View>
    </View>
  );
}

function Chip({ on, label, onPress }) {
  return (
    <Pressable onPress={onPress} style={{
      paddingVertical: 6, paddingHorizontal: 10, borderRadius: 999, marginRight: 6,
      borderWidth: 1, borderColor: on ? '#2E5C4E' : '#B8AE95', backgroundColor: on ? '#E8F3EF' : '#F7F0DC'
    }}>
      <Text style={{ fontWeight: '600', color: on ? '#1C3B33' : '#6B664F' }}>{label}</Text>
    </Pressable>
  );
}

function SmallBtn({ label, onPress }) {
  return (
    <Pressable onPress={onPress} style={{ backgroundColor: '#2E5C4E', paddingVertical: 6, paddingHorizontal: 12, borderRadius: 8 }}>
      <Text style={{ color: 'white', fontWeight: '700', fontSize: 12 }}>{label}</Text>
    </Pressable>
  );
}

